import * as React from "react";
import {
  Box,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Company } from "../../types/company";
import { useNavigate, useParams } from "react-router-dom";
import {
  useAddCompanyMutation,
  useEditCompanyMutation,
  useGetCompanyByIdQuery,
} from "../../services/company.service";
import { useTitle } from "../../hooks/useTitle";
import { Controller, useForm } from "react-hook-form";
import useScroll from "../../hooks/useScroll";
import { toast } from "react-toastify";
import SelectCountry from "../../components/SelectCountry";
import SelectVertical from "../../components/SelectVertical";
import StickyBox from "react-sticky-box";
import { isManage360 } from "../../utils/helper";
import UploadLogo from "./UploadLogo";
import { CardBase, StyledTextField, SaveButton, OrangeLinearProgress } from "../../components/global";

const CompanyEditPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const _isManage360 = React.useMemo(() => isManage360(), []);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [scrollToBasic, basicRef] = useScroll();
  const [scrollToSetting, settingRef] = useScroll();
  const [scrollToContact, contactRef] = useScroll();
  const [scrollToBilling, billingRef] = useScroll();
  const [scrollToSocial, socialRef] = useScroll();

  const { data, isLoading } = useGetCompanyByIdQuery(parseInt(companyId!), {
    skip: !companyId,
  });
  const [add, { isLoading: adding }] = useAddCompanyMutation();
  const [edit, { isLoading: updating }] = useEditCompanyMutation();

  useTitle(!!data ? "Edit Client" : "Create Client");

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm();

  const onSubmit = async (payload: any) => {
    try {
      if (!data) {
        const company: Company = await add(payload).unwrap();
        toast.success("Client created");
        navigate(`/clients/${company.id}`, { replace: true });
      } else {
        if (!payload.password) delete payload.password;
        payload.id = data.id;
        await edit(payload).unwrap();
        toast.success("Client updated");
        navigate(`/clients/${data.id}`, { replace: true });
      }
    } catch (err) { }
  };

  React.useEffect(() => {
    setValue("name", data?.name || "");
    setValue("phone_number", data?.phone_number || "");
    setValue("fax", data?.fax || "");
    setValue("headquarters_address_1", data?.headquarters_address_1 || "");
    setValue("headquarters_address_2", data?.headquarters_address_2 || "");
    setValue("country", data?.country_id || 0);
    setValue("city", data?.city || "");
    setValue("state", data?.state || "");
    setValue("zip", data?.zip || "");
    setValue("linked_in", data?.linked_in || "");
    setValue("facebook", data?.facebook || "");
    setValue("instagram", data?.instagram || "");
    setValue("website", data?.website || "");
    setValue("logo", data?.logo?.[0]?.original_url || "");
    setValue("primary_contact_name", data?.primary_contact_name || "");
    setValue("primary_contact_title", data?.primary_contact_title || "");
    setValue("primary_contact_email", data?.primary_contact_email || "");
    setValue(
      "primary_contact_phone_number",
      data?.primary_contact_phone_number || ""
    );
    setValue("business_vertical", data?.business_vertical_id || 0);
    setValue("color_schema", data?.color_schema || theme.palette.primary.main);
    setValue("billing_email", data?.billing_email);
    setValue("billing_address_1", data?.billing_address_1 || "");
    setValue("billing_address_2", data?.billing_address_2 || "");
    setValue("billing_country", data?.billing_country_id || "");
    setValue("billing_city", data?.billing_city || "");
    setValue("billing_state", data?.billing_state || "");
    setValue("billing_zip", data?.billing_zip || "");
    if (_isManage360) {
      setValue("percentage_uplift", 0);
      setValue("fixed_price_fee", 0);
    } else {
      setValue("percentage_uplift", data?.percentage_uplift || 0);
      setValue("fixed_price_fee", 0); //force set to 0 - not used anymore
    }
  }, [data, _isManage360]);

  return (
    <Box>
      {isLoading && <OrangeLinearProgress position="relative" />}
      <Grid container spacing={isMobile ? 0 : 1}>
        {!isMobile && (
          <Grid item xs={12} sm={3}>
            <StickyBox offsetTop={70} offsetBottom={20}>
              <List>
                <ListItem>
                  <ListItemButton onClick={scrollToBasic as any}>
                    <ListItemText>General</ListItemText>
                  </ListItemButton>
                </ListItem>
                <ListItem>
                  <ListItemButton onClick={scrollToSetting as any}>
                    <ListItemText>Settings</ListItemText>
                  </ListItemButton>
                </ListItem>
                <ListItem>
                  <ListItemButton onClick={scrollToContact as any}>
                    <ListItemText>Primary Contact</ListItemText>
                  </ListItemButton>
                </ListItem>
                <ListItem>
                  <ListItemButton onClick={scrollToBilling as any}>
                    <ListItemText>Billing Address</ListItemText>
                  </ListItemButton>
                </ListItem>
                <ListItem>
                  <ListItemButton onClick={scrollToSocial as any}>
                    <ListItemText>Social Medias</ListItemText>
                  </ListItemButton>
                </ListItem>
              </List>
            </StickyBox>
          </Grid>
        )}
        <Grid item xs={12} sm={isMobile ? 12 : 9}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box>
              {data && (
                <Typography
                  variant="h4"
                  sx={{
                    mb: 3,
                    color: "#F4A259",
                    fontWeight: 600,
                    fontSize: { xs: "1.5rem", sm: "2rem" },
                  }}
                >
                  {data.name}
                </Typography>
              )}
              <CardBase
                title="General"
                useGlassCard={false}
                sx={{ mb: 2 }}
                ref={basicRef}
              >
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Company's Logo
                </Typography>
                <Controller
                  name="logo"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <UploadLogo onChange={onChange} value={value} />
                  )}
                />
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <StyledTextField
                      InputLabelProps={{ shrink: !!value }}
                      label="Company name"
                      value={value || ""}
                      fullWidth
                      onChange={onChange}
                      onBlur={onBlur}
                      inputRef={ref}
                      error={!!errors.name}
                      helperText={!!errors.name && "This field is required"}
                    />
                  )}
                />
                <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
                  <Controller
                    name="phone_number"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <StyledTextField
                        InputLabelProps={{ shrink: !!value }}
                        label="Phone number"
                        value={value || ""}
                        fullWidth
                        onChange={onChange}
                        onBlur={onBlur}
                        sx={{ mt: 0 }}
                        inputRef={ref}
                        error={!!errors.phone_number}
                        helperText={
                          !!errors.phone_number && "This field is required"
                        }
                      />
                    )}
                  />
                  <Controller
                    name="fax"
                    control={control}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <StyledTextField
                        InputLabelProps={{ shrink: !!value }}
                        label="Fax"
                        value={value || ""}
                        fullWidth
                        onChange={onChange}
                        onBlur={onBlur}
                        inputRef={ref}
                      />
                    )}
                  />
                </Stack>
                <Controller
                  name="billing_email"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <StyledTextField
                      InputLabelProps={{ shrink: !!value }}
                      label="Email (for billings etc)"
                      value={value || ""}
                      fullWidth
                      onChange={onChange}
                      onBlur={onBlur}
                      inputRef={ref}
                      error={!!errors.billing_email}
                      helperText={
                        !!errors.billing_email && "This field is required"
                      }
                    />
                  )}
                />
                <Controller
                  name="headquarters_address_1"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <StyledTextField
                      InputLabelProps={{ shrink: !!value }}
                      label="Headquarter Address 1"
                      value={value || ""}
                      fullWidth
                      onChange={onChange}
                      onBlur={onBlur}
                      inputRef={ref}
                      error={!!errors.headquarters_address_1}
                      helperText={
                        !!errors.headquarters_address_1 &&
                        "This field is required"
                      }
                    />
                  )}
                />
                <Controller
                  name="headquarters_address_2"
                  control={control}
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <StyledTextField
                      InputLabelProps={{ shrink: !!value }}
                      label="Headquarter Address 2 (optional)"
                      value={value || ""}
                      fullWidth
                      onChange={onChange}
                      onBlur={onBlur}
                      inputRef={ref}
                    />
                  )}
                />
                <Stack direction={"row"} spacing={1} sx={{ mt: 2 }}>
                  <Controller
                    name="city"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <StyledTextField
                        InputLabelProps={{ shrink: !!value }}
                        label="City"
                        value={value || ""}
                        fullWidth
                        onChange={onChange}
                        onBlur={onBlur}
                        sx={{ mt: 0 }}
                        inputRef={ref}
                        error={!!errors.city}
                        helperText={!!errors.city && "This field is required"}
                      />
                    )}
                  />
                  <Controller
                    name="state"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <StyledTextField
                        InputLabelProps={{ shrink: !!value }}
                        label="State"
                        value={value || ""}
                        fullWidth
                        onChange={onChange}
                        onBlur={onBlur}
                        inputRef={ref}
                        error={!!errors.state}
                        helperText={
                          !!errors.state && "This field is required"
                        }
                      />
                    )}
                  />
                  <Controller
                    name="zip"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <StyledTextField
                        InputLabelProps={{ shrink: !!value }}
                        label="Zip"
                        value={value || ""}
                        fullWidth
                        onChange={onChange}
                        onBlur={onBlur}
                        inputRef={ref}
                        error={!!errors.zip}
                        helperText={!!errors.zip && "This field is required"}
                      />
                    )}
                  />
                </Stack>
                <Controller
                  name="country"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <SelectCountry onChange={onChange} value={value} />
                  )}
                />
              </CardBase>
              <CardBase
                title="Company Settings"
                useGlassCard={false}
                sx={{ mb: 2 }}
                ref={settingRef}
              >
                <Controller
                  name="business_vertical"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <SelectVertical onChange={onChange} value={value} />
                  )}
                />
                {!_isManage360 && (
                  <>
                    <Controller
                      name="percentage_uplift"
                      control={control}
                      rules={{ required: true, min: 0, max: 100 }}
                      render={({
                        field: { onChange, onBlur, value, ref },
                      }) => (
                        <StyledTextField
                          InputLabelProps={{ shrink: true }}
                          label="Uplift Percentage (%)"
                          value={value || ""}
                          fullWidth
                          onChange={onChange}
                          onBlur={onBlur}
                          inputRef={ref}
                          type="number"
                          error={!!errors.percentage_uplift}
                          helperText={
                            !!errors.percentage_uplift &&
                            "This field is required"
                          }
                        />
                      )}
                    />
                    {/* <Controller
                        name="fixed_price_fee"
                        control={control}
                        rules={{ required: true, min: 0, max: 1000 }}
                        render={({
                          field: { onChange, onBlur, value, ref },
                        }) => (
                          <TextField
                            InputLabelProps={{ shrink: true }}
                            label="Fixed Fee"
                            value={value}
                            fullWidth
                            onChange={onChange}
                            onBlur={onBlur}
                            type="number"
                            error={!!errors.fixed_price_fee}
                            helperText={
                              !!errors.fixed_price_fee &&
                              "This field is required"
                            }
                          />
                        )}
                      /> */}
                  </>
                )}
                <Controller
                  name="color_schema"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <StyledTextField
                      InputLabelProps={{ shrink: !!value }}
                      label="Color Scheme"
                      value={value || ""}
                      fullWidth
                      onChange={onChange}
                      onBlur={onBlur}
                      inputRef={ref}
                      error={!!errors.color_schema}
                      helperText={
                        !!errors.color_schema && "This field is required"
                      }
                    />
                  )}
                />
              </CardBase>
              <CardBase
                title="Contact Person"
                useGlassCard={false}
                sx={{ mb: 2 }}
                ref={contactRef}
              >
                <Stack direction="row" spacing={1}>
                  <Controller
                    name="primary_contact_name"
                    control={control}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <StyledTextField
                        InputLabelProps={{ shrink: !!value }}
                        label="Contact name (optional)"
                        value={value || ""}
                        fullWidth
                        sx={{ mt: 0 }}
                        onChange={onChange}
                        onBlur={onBlur}
                        inputRef={ref}
                      />
                    )}
                  />
                  <Controller
                    name="primary_contact_title"
                    control={control}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <StyledTextField
                        InputLabelProps={{ shrink: !!value }}
                        label="Title (optional)"
                        value={value || ""}
                        fullWidth
                        onChange={onChange}
                        onBlur={onBlur}
                        inputRef={ref}
                      />
                    )}
                  />
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  <Controller
                    name="primary_contact_email"
                    control={control}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <StyledTextField
                        InputLabelProps={{ shrink: !!value }}
                        label="Contact email (optional)"
                        value={value || ""}
                        fullWidth
                        sx={{ mt: 0 }}
                        onChange={onChange}
                        onBlur={onBlur}
                        inputRef={ref}
                      />
                    )}
                  />
                  <Controller
                    name="primary_contact_phone_number"
                    control={control}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <StyledTextField
                        InputLabelProps={{ shrink: !!value }}
                        label="Phonenumber (optional)"
                        value={value || ""}
                        fullWidth
                        onChange={onChange}
                        onBlur={onBlur}
                        inputRef={ref}
                      />
                    )}
                  />
                </Stack>
              </CardBase>
              <CardBase
                title="Billing Address"
                useGlassCard={false}
                sx={{ mb: 2 }}
                ref={billingRef}
              >
                <Controller
                  name="billing_address_1"
                  control={control}
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <StyledTextField
                      InputLabelProps={{ shrink: !!value }}
                      label="Billing Address 1 (optional)"
                      value={value || ""}
                      fullWidth
                      onChange={onChange}
                      onBlur={onBlur}
                      inputRef={ref}
                    />
                  )}
                />
                <Controller
                  name="billing_address_2"
                  control={control}
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <StyledTextField
                      InputLabelProps={{ shrink: !!value }}
                      label="Billing Address 2 (optional)"
                      value={value || ""}
                      fullWidth
                      onChange={onChange}
                      onBlur={onBlur}
                      inputRef={ref}
                    />
                  )}
                />
                <Stack direction={"row"} spacing={1} sx={{ mt: 2 }}>
                  <Controller
                    name="billing_city"
                    control={control}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <StyledTextField
                        InputLabelProps={{ shrink: !!value }}
                        label="City (optional)"
                        value={value || ""}
                        fullWidth
                        onChange={onChange}
                        onBlur={onBlur}
                        sx={{ mt: 0 }}
                        inputRef={ref}
                      />
                    )}
                  />
                  <Controller
                    name="billing_state"
                    control={control}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <StyledTextField
                        InputLabelProps={{ shrink: !!value }}
                        label="State (optional)"
                        value={value || ""}
                        fullWidth
                        onChange={onChange}
                        onBlur={onBlur}
                        inputRef={ref}
                      />
                    )}
                  />
                  <Controller
                    name="billing_zip"
                    control={control}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <StyledTextField
                        InputLabelProps={{ shrink: !!value }}
                        label="Zip (optional)"
                        value={value || ""}
                        fullWidth
                        onChange={onChange}
                        onBlur={onBlur}
                        inputRef={ref}
                      />
                    )}
                  />
                </Stack>
                <Controller
                  name="billing_country"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <SelectCountry onChange={onChange} value={value} />
                  )}
                />
              </CardBase>
              <CardBase
                title="Social Medias"
                useGlassCard={false}
                sx={{ mb: 2 }}
                ref={socialRef}
              >
                <Controller
                  name="linked_in"
                  control={control}
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <StyledTextField
                      InputLabelProps={{ shrink: !!value }}
                      label="LinkedIn URL (optional)"
                      value={value || ""}
                      fullWidth
                      onChange={onChange}
                      onBlur={onBlur}
                      inputRef={ref}
                    />
                  )}
                />
                <Controller
                  name="facebook"
                  control={control}
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <StyledTextField
                      InputLabelProps={{ shrink: !!value }}
                      label="Facebook URL (optional)"
                      value={value || ""}
                      fullWidth
                      onChange={onChange}
                      onBlur={onBlur}
                      inputRef={ref}
                    />
                  )}
                />
                <Controller
                  name="instagram"
                  control={control}
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <StyledTextField
                      InputLabelProps={{ shrink: !!value }}
                      label="Instagram URL (optional)"
                      value={value || ""}
                      fullWidth
                      onChange={onChange}
                      onBlur={onBlur}
                      inputRef={ref}
                    />
                  )}
                />
                <Controller
                  name="website"
                  control={control}
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <StyledTextField
                      InputLabelProps={{ shrink: !!value }}
                      label="Website URL (optional)"
                      value={value || ""}
                      fullWidth
                      onChange={onChange}
                      onBlur={onBlur}
                      inputRef={ref}
                    />
                  )}
                />
              </CardBase>
              <Stack
                direction="row"
                justifyContent={isMobile ? "stretch" : "flex-end"}
                sx={{ mt: 2 }}
              >
                <SaveButton
                  disabled={isLoading}
                  loading={adding || updating}
                  isUpdate={!!data}
                  saveLabel="Create Client"
                  updateLabel="Update Client"
                />
              </Stack>
            </Box>
          </form>
        </Grid>
      </Grid>
    </Box>
  );
};
export default CompanyEditPage;
